import useAsyncFn from 'react-use/lib/useAsyncFn';
import { useApi } from '@backstage/core-plugin-api';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Text,
} from '@backstage/ui';
import { sonarqubeApiRef } from '../../../api/sonarqube-api';
import styles from './SonarQubeServiceGroupPage.module.css';

/** The member to remove; `undefined` keeps the dialog closed. */
export interface MemberToRemove {
  entraId: string;
  name: string;
}

interface RemoveGroupMemberDialogProps {
  serviceGroupId: string;
  member: MemberToRemove | undefined;
  onClose: () => void;
  onRemoved: () => void;
}

export const RemoveGroupMemberDialog = ({
  serviceGroupId,
  member,
  onClose,
  onRemoved,
}: RemoveGroupMemberDialogProps) => {
  const sonarqubeApi = useApi(sonarqubeApiRef);
  const [submitState, submit] = useAsyncFn(async () => {
    if (!member) return;
    await sonarqubeApi.removeUserFromServiceGroup(
      serviceGroupId,
      member.entraId,
    );
    onRemoved();
  }, [sonarqubeApi, serviceGroupId, member, onRemoved]);

  const close = () => {
    if (!submitState.loading) onClose();
  };

  return (
    <Dialog
      isOpen={!!member}
      onOpenChange={isOpen => {
        if (!isOpen) close();
      }}
      width={480}
    >
      <DialogHeader>
        Are you sure you want to remove {member?.name} from this tooling group?
      </DialogHeader>
      <DialogBody className={styles.dialogBody}>
        <Text as="p" color="secondary">
          They lose access to the tooling group's SonarQube projects.
        </Text>
        {submitState.error && (
          <Text color="danger" as="p">
            {submitState.error.message}
          </Text>
        )}
      </DialogBody>
      <DialogFooter>
        <Button
          variant="secondary"
          onClick={close}
          isDisabled={submitState.loading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          destructive
          onClick={submit}
          isDisabled={submitState.loading}
          isPending={submitState.loading}
        >
          Remove member
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
